package com.daytimegaming.ourdailyfinances.presentation.navigation

import androidx.compose.animation.SharedTransitionLayout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.navigation3.runtime.NavBackStack
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.ui.LocalNavAnimatedContentScope
import androidx.navigation3.ui.NavDisplay
import androidx.savedstate.serialization.SavedStateConfiguration
import com.daytimegaming.ourdailyfinances.presentation.account.AccountDetailScreen
import com.daytimegaming.ourdailyfinances.presentation.account.AddRealEstateHoldingScreen
import com.daytimegaming.ourdailyfinances.presentation.account.ViewRealEstateHoldingScreen
import com.daytimegaming.ourdailyfinances.presentation.account.UpdateRealEstateHoldingScreen
import com.daytimegaming.ourdailyfinances.presentation.account.SecurityHoldingDetailScreen
import com.daytimegaming.ourdailyfinances.presentation.account.TransactionDetailScreen
import com.daytimegaming.ourdailyfinances.presentation.auth.LoginScreen
import com.daytimegaming.ourdailyfinances.presentation.auth.RegisterScreen
import com.daytimegaming.ourdailyfinances.presentation.auth.SplashScreen
import com.daytimegaming.ourdailyfinances.presentation.auth.UpdateUserInfoScreen
import com.daytimegaming.ourdailyfinances.presentation.auth.LandingScreen
import com.daytimegaming.ourdailyfinances.getPlatform
import com.daytimegaming.ourdailyfinances.presentation.dashboard.DashboardDetailScreen
import com.daytimegaming.ourdailyfinances.presentation.dashboard.CreateDashboardScreen
import com.daytimegaming.ourdailyfinances.presentation.dashboard.JoinDashboardScreen
import com.daytimegaming.ourdailyfinances.presentation.dashboard.ConfigureDashboardScreen
import com.daytimegaming.ourdailyfinances.presentation.dashboard.DashboardTransactionsScreen
import com.daytimegaming.ourdailyfinances.presentation.home.HomeScreen
import com.daytimegaming.ourdailyfinances.presentation.home.RecentSpendingScreen
import com.daytimegaming.ourdailyfinances.presentation.home.CategorySpendingScreen
import com.daytimegaming.ourdailyfinances.presentation.home.SpendingRange
import com.daytimegaming.ourdailyfinances.presentation.home.RecurringTransactionsScreen
import com.daytimegaming.ourdailyfinances.presentation.home.RecurringTransactionDetailScreen
import com.daytimegaming.ourdailyfinances.presentation.home.IncomeVsExpensesSummaryScreen
import com.daytimegaming.ourdailyfinances.presentation.home.MonthlyBreakdownScreen
import com.daytimegaming.ourdailyfinances.presentation.manageaccounts.ManageAccountsScreen
import com.daytimegaming.ourdailyfinances.presentation.budget.CreateBudgetScreen
import com.daytimegaming.ourdailyfinances.presentation.budget.ViewBudgetScreen
import com.daytimegaming.ourdailyfinances.presentation.budget.UpdateBudgetScreen
import com.daytimegaming.ourdailyfinances.presentation.home.ManageSubscriptionScreen
import com.daytimegaming.ourdailyfinances.presentation.legal.PrivacyPolicyScreen
import com.daytimegaming.ourdailyfinances.presentation.legal.TermsOfServiceScreen
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic

private val navSavedStateConfig = SavedStateConfiguration {
    serializersModule = SerializersModule {
        polymorphic(NavKey::class) {
            subclass(AppRoute.Login::class, AppRoute.Login.serializer())
            subclass(AppRoute.AddRealEstateHolding::class, AppRoute.AddRealEstateHolding.serializer())
            subclass(AppRoute.ViewRealEstateHolding::class, AppRoute.ViewRealEstateHolding.serializer())
            subclass(AppRoute.UpdateRealEstateHolding::class, AppRoute.UpdateRealEstateHolding.serializer())
            subclass(AppRoute.Register::class, AppRoute.Register.serializer())
            subclass(AppRoute.UpdateUserInfo::class, AppRoute.UpdateUserInfo.serializer())
            subclass(AppRoute.Home::class, AppRoute.Home.serializer())
            subclass(AppRoute.DashboardDetail::class, AppRoute.DashboardDetail.serializer())
            subclass(AppRoute.AccountDetail::class, AppRoute.AccountDetail.serializer())
            subclass(AppRoute.SecurityHoldingDetail::class, AppRoute.SecurityHoldingDetail.serializer())
            subclass(AppRoute.ManageAccounts::class, AppRoute.ManageAccounts.serializer())
            subclass(AppRoute.CreateDashboard::class, AppRoute.CreateDashboard.serializer())
            subclass(AppRoute.JoinDashboard::class, AppRoute.JoinDashboard.serializer())
            subclass(AppRoute.ConfigureDashboard::class, AppRoute.ConfigureDashboard.serializer())
            subclass(AppRoute.DashboardTransactions::class, AppRoute.DashboardTransactions.serializer())
            subclass(AppRoute.RecentSpending::class, AppRoute.RecentSpending.serializer())
            subclass(AppRoute.RecurringTransactions::class, AppRoute.RecurringTransactions.serializer())
            subclass(AppRoute.IncomeVsExpensesSummary::class, AppRoute.IncomeVsExpensesSummary.serializer())
            subclass(AppRoute.MonthlyBreakdown::class, AppRoute.MonthlyBreakdown.serializer())
            subclass(AppRoute.CategorySpending::class, AppRoute.CategorySpending.serializer())
            subclass(AppRoute.TransactionDetail::class, AppRoute.TransactionDetail.serializer())
            subclass(AppRoute.RecurringTransactionDetail::class, AppRoute.RecurringTransactionDetail.serializer())
            subclass(AppRoute.CreateBudget::class, AppRoute.CreateBudget.serializer())
            subclass(AppRoute.ViewBudget::class, AppRoute.ViewBudget.serializer())
            subclass(AppRoute.UpdateBudget::class, AppRoute.UpdateBudget.serializer())
            subclass(AppRoute.Splash::class, AppRoute.Splash.serializer())
            subclass(AppRoute.Landing::class, AppRoute.Landing.serializer())
            subclass(AppRoute.ManageSubscription::class, AppRoute.ManageSubscription.serializer())
            subclass(AppRoute.PrivacyPolicy::class, AppRoute.PrivacyPolicy.serializer())
            subclass(AppRoute.TermsOfService::class, AppRoute.TermsOfService.serializer())
        }
    }
}

@Composable
fun AppNavigation(
    isInitializing: Boolean,
    isAuthenticated: Boolean,
    isProfileComplete: Boolean?,
    userEmail: String,
    onPlaidTokenReady: (String) -> Unit,
) {
    SharedTransitionLayout {
        val backStack: NavBackStack<NavKey> =
            rememberNavBackStack(navSavedStateConfig, AppRoute.Splash)

        BrowserNavigationSync(backStack, isAuthenticated)

        val initialWebRoute = remember { getInitialWebRoute() }
        var hasHandledInitialWebRoute by remember { mutableStateOf(false) }

        val safePop: (Int) -> Unit = { count ->
            if (getPlatform().name == "Web Browser") {
                if (backStack.size > count) {
                    triggerBrowserBack(count)
                } else {
                    backStack.clear()
                    val target = if (isAuthenticated) AppRoute.Home() else AppRoute.Landing
                    backStack.add(target)
                }
            } else {
                var popped = 0
                while (popped < count && backStack.size > 1) {
                    backStack.removeLastOrNull()
                    popped++
                }
                if (popped < count) {
                    backStack.clear()
                    val target = if (isAuthenticated) AppRoute.Home() else AppRoute.Login
                    backStack.add(target)
                }
            }
        }
        val safeNavigateBack: () -> Unit = { safePop(1) }

        LaunchedEffect(isInitializing, isAuthenticated, isProfileComplete) {
            if (isInitializing) {
                val top = backStack.lastOrNull()
                if (top != AppRoute.Splash) {
                    backStack.clear()
                    backStack.add(AppRoute.Splash)
                }
                return@LaunchedEffect
            }

            val top = backStack.lastOrNull()
            if (!isAuthenticated) {
                val isUnauthenticatedAllowed = top is AppRoute.Login ||
                        top is AppRoute.Register ||
                        top is AppRoute.Landing ||
                        top is AppRoute.PrivacyPolicy ||
                        top is AppRoute.TermsOfService
                if (top != null && !isUnauthenticatedAllowed) {
                    val isPublicRoute = initialWebRoute is AppRoute.Login ||
                            initialWebRoute is AppRoute.Register ||
                            initialWebRoute is AppRoute.Landing ||
                            initialWebRoute is AppRoute.PrivacyPolicy ||
                            initialWebRoute is AppRoute.TermsOfService
                    val requiredRoute = if (isPublicRoute) {
                        initialWebRoute
                    } else {
                        if (getPlatform().name == "Web Browser") AppRoute.Landing else AppRoute.Login
                    }
                    backStack.clear()
                    backStack.add(requiredRoute)
                }
            } else {
                if (isProfileComplete == true) {
                    if (top is AppRoute.Login || top is AppRoute.Register || top is AppRoute.UpdateUserInfo || top == AppRoute.Splash) {
                        val isProtectedRoute = initialWebRoute != null &&
                                initialWebRoute !is AppRoute.Splash &&
                                initialWebRoute !is AppRoute.Landing &&
                                initialWebRoute !is AppRoute.Login &&
                                initialWebRoute !is AppRoute.Register &&
                                initialWebRoute !is AppRoute.PrivacyPolicy &&
                                initialWebRoute !is AppRoute.TermsOfService

                        val targetRoute = if (!hasHandledInitialWebRoute && isProtectedRoute) {
                            hasHandledInitialWebRoute = true
                            initialWebRoute
                        } else {
                            AppRoute.Home()
                        }
                        backStack.clear()
                        backStack.add(targetRoute)
                    }
                } else if (isProfileComplete == false) {
                    if (top !is AppRoute.UpdateUserInfo) {
                        backStack.clear()
                        backStack.add(AppRoute.UpdateUserInfo(userEmail))
                    }
                }
            }
        }

        NavDisplay(
            backStack = backStack,
            onBack = safeNavigateBack,
            entryProvider = entryProvider {
                entry<AppRoute.Splash> {
                    SplashScreen(
                        sharedTransitionScope = this@SharedTransitionLayout,
                        animatedVisibilityScope = LocalNavAnimatedContentScope.current,
                    )
                }
                entry<AppRoute.Landing> {
                    LandingScreen(
                        sharedTransitionScope = this@SharedTransitionLayout,
                        animatedVisibilityScope = LocalNavAnimatedContentScope.current,
                        onNavigateToLogin = { backStack.add(AppRoute.Login) },
                        onNavigateToRegister = { backStack.add(AppRoute.Register) },
                        onNavigateToPrivacyPolicy = { backStack.add(AppRoute.PrivacyPolicy) },
                        onNavigateToTermsOfService = { backStack.add(AppRoute.TermsOfService) }
                    )
                }
                entry<AppRoute.PrivacyPolicy> {
                    PrivacyPolicyScreen(
                        onNavigateBack = safeNavigateBack
                    )
                }
                entry<AppRoute.TermsOfService> {
                    TermsOfServiceScreen(
                        onNavigateBack = safeNavigateBack
                    )
                }
                entry<AppRoute.Login> {
                    LoginScreen(
                        sharedTransitionScope = this@SharedTransitionLayout,
                        animatedVisibilityScope = LocalNavAnimatedContentScope.current,
                        onNavigateToRegister = { backStack.add(AppRoute.Register) },
                    )
                }
                entry<AppRoute.Register> {
                    RegisterScreen(
                        sharedTransitionScope = this@SharedTransitionLayout,
                        animatedVisibilityScope = LocalNavAnimatedContentScope.current,
                        onNavigateBack = safeNavigateBack,
                    )
                }
                entry<AppRoute.UpdateUserInfo> { route ->
                    UpdateUserInfoScreen(
                        email = route.email,
                        sharedTransitionScope = this@SharedTransitionLayout,
                        animatedVisibilityScope = LocalNavAnimatedContentScope.current,
                        onSuccess = {
                            // LaunchedEffect will automatically transition to Home on success
                        }
                    )
                }
                entry<AppRoute.Home> { route ->
                    HomeScreen(
                        initialTabName = route.tab,
                        onTabChanged = { tabName ->
                            val top = backStack.lastOrNull()
                            if (top is AppRoute.Home && top.tab != tabName) {
                                backStack.removeLastOrNull()
                                backStack.add(AppRoute.Home(tabName))
                            }
                        },
                        onDashboardClick = { dashboardId ->
                            backStack.add(AppRoute.DashboardDetail(dashboardId))
                        },
                        onAccountClick = { accountId ->
                            backStack.add(AppRoute.AccountDetail(accountId))
                        },
                        onRealEstateHoldingClick = { holdingId ->
                            backStack.add(AppRoute.ViewRealEstateHolding(holdingId))
                        },
                        onPlaidTokenReady = onPlaidTokenReady,
                        onManageAccounts = {
                            backStack.add(AppRoute.ManageAccounts)
                        },
                        onAddRealEstateHolding = {
                            backStack.add(AppRoute.AddRealEstateHolding)
                        },
                        onCreateDashboard = {
                            backStack.add(AppRoute.CreateDashboard)
                        },
                        onJoinDashboard = {
                            backStack.add(AppRoute.JoinDashboard)
                        },
                        onViewRecentSpending = {
                            backStack.add(AppRoute.RecentSpending)
                        },
                        onViewRecurringTransactions = {
                            backStack.add(AppRoute.RecurringTransactions)
                        },
                        onViewIncomeVsExpenses = {
                            backStack.add(AppRoute.IncomeVsExpensesSummary)
                        },
                        onCreateBudget = {
                            backStack.add(AppRoute.CreateBudget)
                        },
                        onBudgetClick = { budgetId ->
                            backStack.add(AppRoute.ViewBudget(budgetId))
                        },
                        onTransactionClick = { transactionId ->
                            backStack.add(AppRoute.TransactionDetail(transactionId))
                        },
                        onManageSubscription = {
                            backStack.add(AppRoute.ManageSubscription)
                        }
                    )
                }
                entry<AppRoute.ManageSubscription> {
                    ManageSubscriptionScreen(
                        onNavigateBack = safeNavigateBack
                    )
                }
                entry<AppRoute.AddRealEstateHolding> {
                    AddRealEstateHoldingScreen(
                        onNavigateBack = safeNavigateBack
                    )
                }
                entry<AppRoute.ViewRealEstateHolding> { route ->
                    ViewRealEstateHoldingScreen(
                        realEstateHoldingId = route.realEstateHoldingId,
                        onNavigateBack = safeNavigateBack,
                        onConfigureClick = { id ->
                            backStack.add(AppRoute.UpdateRealEstateHolding(id))
                        },
                        onInvalidAccess = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.UpdateRealEstateHolding> { route ->
                    UpdateRealEstateHoldingScreen(
                        realEstateHoldingId = route.realEstateHoldingId,
                        onNavigateBack = safeNavigateBack,
                        onHoldingDeleted = {
                            safePop(2)
                        }
                    )
                }
                entry<AppRoute.DashboardDetail> { route ->
                    DashboardDetailScreen(
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onConfigureDashboard = { dashboardId ->
                            backStack.add(AppRoute.ConfigureDashboard(dashboardId))
                        },
                        onViewAllTransactions = { dashboardId ->
                            backStack.add(AppRoute.DashboardTransactions(dashboardId))
                        },
                        onAccountClick = { accountId ->
                            backStack.add(
                                AppRoute.AccountDetail(
                                    accountId,
                                    dashboardId = route.dashboardId
                                )
                            )
                        },
                        onRealEstateHoldingClick = { holdingId ->
                            backStack.add(AppRoute.ViewRealEstateHolding(holdingId))
                        },
                        onTransactionClick = { transactionId ->
                            backStack.add(
                                AppRoute.TransactionDetail(
                                    transactionId,
                                    dashboardId = route.dashboardId
                                )
                            )
                        },
                        onInvalidAccess = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.ConfigureDashboard> { route ->
                    ConfigureDashboardScreen(
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onDashboardLeft = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        },
                        onDashboardDeleted = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.DashboardTransactions> { route ->
                    DashboardTransactionsScreen(
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onTransactionClick = { transactionId ->
                            backStack.add(
                                AppRoute.TransactionDetail(
                                    transactionId,
                                    dashboardId = route.dashboardId
                                )
                            )
                        }
                    )
                }
                entry<AppRoute.AccountDetail> { route ->
                    AccountDetailScreen(
                        accountId = route.accountId,
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onTransactionClick = { transactionId ->
                            backStack.add(
                                AppRoute.TransactionDetail(
                                    transactionId,
                                    dashboardId = route.dashboardId
                                )
                            )
                        },
                        onHoldingClick = { securityId ->
                            backStack.add(
                                AppRoute.SecurityHoldingDetail(
                                    accountId = route.accountId,
                                    securityId = securityId,
                                    dashboardId = route.dashboardId
                                )
                            )
                        },
                        onInvalidAccess = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.SecurityHoldingDetail> { route ->
                    SecurityHoldingDetailScreen(
                        accountId = route.accountId,
                        securityId = route.securityId,
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onInvalidAccess = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.ManageAccounts> {
                    ManageAccountsScreen(
                        onNavigateBack = safeNavigateBack,
                        onPlaidTokenReady = onPlaidTokenReady,
                    )
                }
                entry<AppRoute.CreateDashboard> {
                    CreateDashboardScreen(
                        onNavigateBack = safeNavigateBack,
                        onDashboardCreated = { dashboardId ->
                            safePop(1)
                            backStack.add(AppRoute.DashboardDetail(dashboardId))
                        }
                    )
                }
                entry<AppRoute.JoinDashboard> {
                    JoinDashboardScreen(
                        onNavigateBack = safeNavigateBack,
                        onDashboardJoined = { dashboardId ->
                            safePop(1)
                            backStack.add(AppRoute.DashboardDetail(dashboardId))
                        }
                    )
                }
                entry<AppRoute.RecentSpending> {
                    RecentSpendingScreen(
                        onNavigateBack = safeNavigateBack,
                        onNavigateToCategorySpending = { categoryName, range ->
                            backStack.add(AppRoute.CategorySpending(categoryName, range.name))
                        }
                    )
                }
                entry<AppRoute.IncomeVsExpensesSummary> {
                    IncomeVsExpensesSummaryScreen(
                        onNavigateBack = safeNavigateBack,
                        onMonthClick = { yearMonth ->
                            backStack.add(AppRoute.MonthlyBreakdown(yearMonth))
                        }
                    )
                }
                entry<AppRoute.MonthlyBreakdown> { route ->
                    MonthlyBreakdownScreen(
                        yearMonth = route.yearMonth,
                        onNavigateBack = safeNavigateBack,
                        onTransactionClick = { transactionId, dashboardId ->
                            backStack.add(AppRoute.TransactionDetail(transactionId, dashboardId))
                        }
                    )
                }
                entry<AppRoute.CategorySpending> { route ->
                    val range = SpendingRange.valueOf(route.range)
                    CategorySpendingScreen(
                        categoryName = route.categoryName,
                        range = range,
                        onNavigateBack = safeNavigateBack,
                        onTransactionClick = { transactionId, dashboardId ->
                            backStack.add(AppRoute.TransactionDetail(transactionId, dashboardId))
                        }
                    )
                }
                entry<AppRoute.TransactionDetail> { route ->
                    TransactionDetailScreen(
                        transactionId = route.transactionId,
                        dashboardId = route.dashboardId,
                        onNavigateBack = safeNavigateBack,
                        onInvalidAccess = {
                            backStack.clear()
                            backStack.add(AppRoute.Home())
                        }
                    )
                }
                entry<AppRoute.RecurringTransactions> {
                    RecurringTransactionsScreen(
                        onNavigateBack = safeNavigateBack,
                        onStreamClick = { streamId ->
                            backStack.add(AppRoute.RecurringTransactionDetail(streamId))
                        }
                    )
                }
                entry<AppRoute.RecurringTransactionDetail> { route ->
                    RecurringTransactionDetailScreen(
                        streamId = route.streamId,
                        onNavigateBack = safeNavigateBack,
                        onTransactionClick = { transactionId, dashboardId ->
                            backStack.add(AppRoute.TransactionDetail(transactionId, dashboardId))
                        }
                    )
                }
                entry<AppRoute.CreateBudget> {
                    CreateBudgetScreen(
                        onNavigateBack = safeNavigateBack
                    )
                }
                entry<AppRoute.ViewBudget> { route ->
                    ViewBudgetScreen(
                        budgetId = route.budgetId,
                        onNavigateBack = safeNavigateBack,
                        onConfigureClick = { budgetId ->
                            backStack.add(AppRoute.UpdateBudget(budgetId))
                        },
                        onTransactionClick = { transactionId ->
                            backStack.add(AppRoute.TransactionDetail(transactionId))
                        }
                    )
                }
                entry<AppRoute.UpdateBudget> { route ->
                    UpdateBudgetScreen(
                        budgetId = route.budgetId,
                        onNavigateBack = safeNavigateBack,
                        onBudgetDeleted = {
                            safePop(2)
                        }
                    )
                }
            },
        )
    }
}
